// @flow
import React from 'react'
import ReactHighcharts from 'react-highcharts'
import HighchartsMore from 'highcharts/highcharts-more'

HighchartsMore(ReactHighcharts.Highcharts)

export type PointDataType = Array<{color?: string, name: string, values: Array<number>, yAxis: string}>

export type ChartProps = {
    chartStyle: {
        backgroundColor?: string | null,
        height?: number | null,
        marginBottom?: number,
        marginLeft?: number,
        marginRight?: number,
        marginTop?: number,
        spacingBottom?: number,
        spacingTop?: number,
        width?: number | null,
    },
    iframe: number,
    iframeOrder: Array<number>,
    pointData: PointDataType,
    // List of realizations that correspond to each point
    realizations: string[],
    redraw: boolean,
    setFrame: Function,
    title?: string,
}

function ChartException(message) {
    this.message = message
    this.name = 'ChartException'
}


class ChartPanel extends React.Component<ChartProps> {
    chart: any
    config: any

    static defaultProps = {
        redraw: false,
    }

    shouldComponentUpdate(newProps: ChartProps) {
        let changed = JSON.stringify(this.props.pointData) !== JSON.stringify(newProps.pointData)
        if (!changed) {
            // Just update the realization bar
            this.showRealizationBar(newProps)
        }

        changed = changed || newProps.redraw
        return changed
    }

    showRealizationBar(newProps: ChartProps) {
        const chart = this.chart ? this.chart.getChart() : null
        if (chart) {
            chart.xAxis[0].removePlotLine('pl')
            // look up the x axis point for the realization
            const plotline = this.getPlotLineForRealization(newProps)
            chart.xAxis[0].addPlotLine(plotline)
        }
    }

    getChartOptions(extraOptions: any) {
        return { ...this.props.chartStyle, ...(extraOptions || {}) }
    }

    // eslint-disable-next-line
    getPlotLineForRealization(props: ChartProps) {
        // realization is indexed from 0 where realization rasters are indexed from 1
        const rstr = props.realizations[props.iframe]
        return {
            value: props.iframe,
            color: 'lightblue',
            label: {
                useHTML: true,
                style: {
                    // backgroundColor: 'rgba(0,0,0,.5)',
                    borderRadius: '3px',
                    color: 'rgba(0,0,0,0,0.9)',
                    // fontWeight: 'bold',
                    padding: '3px',
                },
                text: `<b>Realization #${rstr}</b>`,
                rotation: 0,
                x: 0,
                y: 10,
            },
            width: 2,
            id: 'pl',
        }
    }

    getRealizations() {
        const props = this.props
        const series = []
        props.pointData.forEach((pd) => {
            series.push({
                name: pd.name,
                color: pd.color,
                data: pd.values,
                lineWidth: 0,
                marker: {
                    enabled: true,
                    radius: 4,
                },
                states: {
                    hover: {
                        lineWidthPlus: 0,
                    },
                },
                yAxis: pd.yAxis,
            })
        })

        const setFrame = this.props.setFrame
        const config = {
            chart: this.getChartOptions({ type: 'line', zoomType: 'x' }),
            credits: true,
            legend: {
                padding: 0,
                margin: 0,
            },
            plotOptions: {
                series: {
                    cursor: 'pointer',
                    events: {
                        // eslint-disable-next-line
                        click: function (event: any) {
                            const iframe = event.point.x
                            if (setFrame) setFrame(iframe)
                        },
                    },
                },
            },
            title: {
                text: props.title ? props.title : undefined,
            },
            xAxis: {
                categories: props.realizations.map((r, i) => i + 1),
                visible: true,
                title: {
                    text: undefined,
                    enabled: false,
                },
                labels: {
                    enabled: true,
                    rotation: -45,
                },
                plotLines: [this.getPlotLineForRealization(this.props)],
            },
            yAxis: [{
                id: 'grayswan',
                labels: {
                    enabled: true,
                    formatter: function() {
                        return `${this.isFirst ? 'Most White Swannish' : 'Most Gray Swannish'}`
                    },
                    style: {
                        color: 'black',
                        marginLeft: '-50px',
                    },
                },
                opposite: true,
                tickAmount: 2,
                title: {
                    text: 'Gray Swan',
                    enabled: false,
                },
                visible: true,
            }, {
                id: 'values',
                title: {
                    text: 'Realization',
                    enabled: false,
                },
                visible: false,
            }],
            tooltip: {
                // eslint-disable-next-line
                formatter: function () {
                    if (props.iframeOrder) {
                        return `<b>${this.y}</b> (Realization #<b>${props.realizations[this.point.x]}</b>)`
                    }
                },
            },
            series,
        }
        return config
    }

    render() {
        const config = this.getRealizations()
        const style = {
            height: this.props.chartStyle.height || '100%',
            width: '100%',
            display: 'inline-block',
            backgroundColor: 'white',
        }

        if (config) {
            return (
                <div id={'er2_chart'} style={style}>
                    <ReactHighcharts config={config} ref={r => (this.chart = r)} />
                </div>
            )
        }

        return (
            <div id="er2_chart">
                <h4 style={{ textAlign: 'center' }}>No data available for plot</h4>
            </div>
        )
    }
}


export default ChartPanel
